import React from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { logError } from "../lib/api/Api";

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends React.Component<
  React.PropsWithChildren,
  State
> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logError({
      message: error.message,
      stack: error.stack,
      info: { componentStack: info.componentStack, type: "ErrorBoundary" },
      originalError: error,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-gray-950 p-4">
          <div className="w-full max-w-2xl bg-gray-800 rounded-xl p-8 shadow-2xl space-y-6 border border-gray-700 text-center">
            <div className="flex justify-center">
              <div className="bg-red-900/20 p-3 rounded-full">
                <ExclamationTriangleIcon className="h-16 w-16 text-red-500" />
              </div>
            </div>
            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-white">
                Something went wrong
              </h1>
              <p className="text-gray-400">
                An unexpected error occurred. Please reload the page.
              </p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="cursor-pointer px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg transition-colors shadow-lg shadow-indigo-500/20"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
